"use client"

import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import type { TestData } from "@/data/tests"
import {
  InfoTimeIcon,
  InfoRadiationIcon,
  InfoContrastIcon,
  InfoHealthAreasIcon,
  InfoPreparationIcon,
  InfoForWhomIcon,
  GreenCheck,
  BodyFigureIcon,
  ImagingIcon,
} from "@/components/ui/icons"

/* ───────────────────── Quick fact icons ───────────────────── */

/** Compact 20px icon set used by the Quick facts table layout.
 *  Mirrors the icon `type` keys in `TestData.quickFacts` (clock,
 *  no-radiation, body-scan, checklist, person, …). Uses the booking-flow
 *  "Info*" icon family so the table matches the product info / booking
 *  experience, falling back to the DS scan/body icons for the two types
 *  that family doesn't cover. */
function QuickFactTableIcon({ type }: { type: string }) {
  const props = { size: 20, className: "shrink-0 text-neutral-900" }
  switch (type) {
    case "clock":
      return <InfoTimeIcon {...props} />
    case "no-radiation":
      return <InfoRadiationIcon {...props} />
    case "contrast":
      return <InfoContrastIcon {...props} />
    case "droplet":
      return <InfoHealthAreasIcon {...props} />
    case "imaging":
      return <ImagingIcon {...props} />
    case "body-scan":
      return <BodyFigureIcon {...props} />
    case "checklist":
      return <InfoPreparationIcon {...props} />
    case "person":
      return <InfoForWhomIcon {...props} />
    default:
      return <InfoTimeIcon {...props} />
  }
}

const HIGHLIGHT_KEYS = [
  "qfHighlight1",
  "qfHighlight2",
  "qfHighlight3",
  "qfHighlight4",
] as const

/**
 * Quick facts — V06 table layout, shared by every test detail page.
 *
 * Left rail: the summary heading taken from the label-less entry in
 * `facts`, plus the page's CTAs. Right column: a single tinted-surface
 * table with one row per labeled fact (icon + sentence-case label on the
 * left, value + descriptive detail on the right).
 *
 * `mri` switches the left rail to the full-body-MRI benefits checklist
 * (short title + green checks, stretched to the table's height) and leads
 * with the value on mobile.
 */
export function QuickFactsSection({
  facts,
  ctaButtons,
  mri = false,
}: {
  facts: TestData["quickFacts"]
  ctaButtons: ReactNode
  mri?: boolean
}) {
  const tt = useTranslations("tests")
  if (!facts?.length) return null

  const tableRows = facts.filter((qf) => qf.label)
  const summaryFact = facts.find((qf) => !qf.label)

  return (
    <section className="py-16 md:py-20">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div
          className={cn(
            "grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20",
            mri && "lg:items-stretch"
          )}
        >
          {/* Left rail */}
          {mri ? (
            <div className="flex flex-col justify-between">
              <div>
                <h2 className="type-display-md text-neutral-900">
                  {tt("qfHighlightsTitle")}
                </h2>
                <ul className="mt-8 flex flex-col gap-4">
                  {HIGHLIGHT_KEYS.map((key) => (
                    <li key={key} className="flex items-center gap-3">
                      <GreenCheck width={20} height={20} className="shrink-0" />
                      <span className="type-body-lg text-neutral-700">
                        {tt(key as never)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {ctaButtons}
            </div>
          ) : (
            <div className="flex flex-col">
              {summaryFact?.detail && (
                <h2 className="type-display-sm max-w-[460px] text-neutral-900">
                  {tt(summaryFact.detail as never)}
                </h2>
              )}
              {ctaButtons}
            </div>
          )}
          {/* Right table */}
          <div className="rounded-[16px] bg-neutral-100 px-6 py-6 md:px-10 md:py-8">
            <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-[auto_1fr] sm:items-start sm:gap-x-6 sm:gap-y-6">
              {tableRows.map((qf, i) => {
                const rawValue = qf.value
                  ? qf.value.startsWith("qf")
                    ? tt(qf.value as never)
                    : qf.value
                  : ""
                const unit = qf.unit ? tt(qf.unit as never) : ""
                const detail = qf.detail ? tt(qf.detail as never) : ""
                const valuePart = rawValue
                  ? `${rawValue}${unit ? ` ${unit}` : ""}`
                  : ""
                return (
                  <li
                    key={i}
                    className="grid grid-cols-1 gap-1 py-3 sm:contents"
                  >
                    {/* Full-body MRI only: on mobile the value (data) leads
                        and the icon+label sits below it as a muted caption;
                        `sm:` resets to the shared desktop label-left /
                        value-right row. Other tests keep the original layout
                        on every size. */}
                    <div
                      className={cn(
                        "flex items-center gap-4",
                        mri && "order-2 sm:order-none"
                      )}
                    >
                      <QuickFactTableIcon type={qf.icon} />
                      <span
                        className={cn(
                          "type-body-lg lowercase first-letter:uppercase",
                          mri
                            ? "text-neutral-600 sm:text-neutral-900"
                            : "text-neutral-900"
                        )}
                      >
                        {qf.label ? tt(qf.label as never) : ""}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "type-body-lg text-left",
                        mri
                          ? "order-1 text-neutral-900 sm:order-none sm:text-neutral-600"
                          : "text-neutral-600"
                      )}
                    >
                      {valuePart ? (
                        <>
                          <span className="block">{valuePart}</span>
                          {detail}
                        </>
                      ) : (
                        detail
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
